import DataToolbar from '@/components/DataToolbar';
import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { getActiveStore } from "@/lib/scope";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ReportActions from '@/components/ReportActions';
import DraftExcelTools from '@/components/DraftExcelTools';
import OpnameSelection from '@/components/OpnameSelection';
import OpnameHistory from '@/components/OpnameHistory';
import StockMovementTab from '@/components/StockMovementTab';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Trash2, ClipboardCheck, Store, Activity, Boxes, ArrowDownToLine, ArrowUpFromLine, PackagePlus } from "lucide-react";
import { useListNav } from "@/lib/useListNav";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";

export default function Stok() {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["stok", "masuk", "keluar", "opname", "pergerakan"];
  const tab = validTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "stok";

  const load = async () => {
    try {
      const [pr, st, cats] = await Promise.all([
        api.get("/products"), api.get("/stores"), api.get("/categories"),
      ]);
      setProducts(pr.data); setStores(st.data); setCategories(cats.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const activeName = stores.find(store => store.id === getActiveStore())?.name || stores[0]?.name || "—";

  return (
    <>
      <PageHeader title="Inventori" subtitle="Stok saat ini, penerimaan, pengeluaran, opname, dan seluruh pergerakan stok">
        <div className="flex items-center gap-2 rounded-xl bg-ui-card border border-ui-line px-3 py-2" data-testid="active-store-badge">
          <Store className="h-4 w-4 text-brand-readable" />
          <span className="text-xs text-ui-muted">Toko:</span>
          <span className="text-sm font-semibold text-ui-text">{activeName}</span>
        </div>
      </PageHeader>

      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5">
        <Tabs value={tab} onValueChange={value => setSearchParams({ tab: value })}>
          <div className="overflow-x-auto pb-1">
          <TabsList className="bg-ui-card border border-ui-line h-auto min-w-max">
            <TabsTrigger value="stok" data-testid="tab-stok" className="gap-1"><Boxes className="h-4 w-4" /> Stok Saat Ini</TabsTrigger>
            <TabsTrigger value="masuk" data-testid="tab-masuk" className="gap-1"><ArrowDownToLine className="h-4 w-4" /> Stok Masuk</TabsTrigger>
            <TabsTrigger value="keluar" data-testid="tab-keluar" className="gap-1"><ArrowUpFromLine className="h-4 w-4" /> Stok Keluar</TabsTrigger>
            <TabsTrigger value="opname" data-testid="tab-opname" className="gap-1"><ClipboardCheck className="h-4 w-4" /> Opname</TabsTrigger>
            <TabsTrigger value="pergerakan" data-testid="tab-pergerakan" className="gap-1"><Activity className="h-4 w-4" /> Pergerakan</TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="stok" className="mt-5">
            <CurrentStockTab products={products} />
          </TabsContent>
          <TabsContent value="masuk" className="mt-5">
            <StockMovementTab type="masuk" />
          </TabsContent>
          <TabsContent value="keluar" className="mt-5">
            <StockMovementTab type="keluar" />
          </TabsContent>
          <TabsContent value="opname" className="mt-5">
            <OpnameTab products={products} categories={categories} activeName={activeName} onDone={load} />
          </TabsContent>
          <TabsContent value="pergerakan" className="mt-5">
            <StockMovementTab />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function CurrentStockTab({ products }) {
  const [search, setSearch] = useState("");
  const rows = useMemo(() => products.filter(product => {
    const query = search.trim().toLowerCase();
    return !query || product.name.toLowerCase().includes(query) || (product.barcode || "").toLowerCase().includes(query);
  }), [products, search]);
  return <div className="space-y-3">
    
    <DataToolbar label="Alat tabel stok" className="mb-3" actions={<ReportActions title="Stok Saat Ini" headers={['Barang','Barcode','Satuan Dasar','Stok Tersedia','Stok Fisik Sistem','Dicadangkan','Batas Minimum']} rows={rows.map(p => [p.name,p.barcode,p.base_unit,p.stock,p.stock_physical ?? p.stock,p.stock_reserved ?? 0,p.min_stock])} notes={['Stok tersedia sudah memperhitungkan cadangan transfer. Data merupakan snapshot saat ekspor.']} />}>
<div className="relative w-full max-w-xl">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
      <Input data-testid="current-stock-search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Cari nama atau barcode produk…" className="pl-10 bg-ui-card border-ui-line" />
    </div>

</DataToolbar>
    <div className="rounded-xl border border-ui-line overflow-x-auto" data-testid="current-stock-table">
      <table className="w-full min-w-[620px] text-sm">
        <thead className="bg-ui-card text-ui-muted text-xs uppercase"><tr><th className="text-left p-3">Produk</th><th className="text-left p-3">Barcode</th><th className="text-right p-3">Stok</th><th className="text-right p-3">Batas Minimum</th><th className="text-left p-3">Status</th></tr></thead>
        <tbody>{rows.map(product => {
          const stock = Number(product.stock || 0); const minimum = Number(product.min_stock ?? 5);
          const status = stock <= 0 ? "Kosong" : stock <= minimum ? "Menipis" : "Aman";
          const tone = stock <= 0 ? "text-danger-readable bg-danger-soft" : stock <= minimum ? "text-warning-readable bg-warning-soft" : "text-brand-readable bg-brand-soft";
          return <tr key={product.id} className="border-t border-ui-line"><td className="p-3 font-medium">{product.name}</td><td className="p-3 font-mono text-ui-muted">{product.barcode || "-"}</td><td className="p-3 text-right font-mono font-bold">{stock.toLocaleString("id-ID")}</td><td className="p-3 text-right font-mono text-ui-muted">{minimum.toLocaleString("id-ID")}</td><td className="p-3"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${tone}`}>{status}</span></td></tr>;
        })}{rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-ui-muted">Produk tidak ditemukan</td></tr>}</tbody>
      </table>
    </div>
  </div>;
}

function ProductPicker({ products, exclude, onPick, testid }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => (search ? products.filter((p) => (p.name.toLowerCase().includes(search.toLowerCase()) || (p.barcode || "").includes(search)) && !exclude.includes(p.id)).slice(0, 6) : []),
    [search, products, exclude]
  );
  const nav = useListNav(filtered, (p) => { onPick(p); setSearch(""); });
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
      <Input data-testid={testid} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={nav.onInputKeyDown} placeholder="Cari & tambahkan barang…" className="pl-10 bg-ui-card border-ui-line" />
      {filtered.length > 0 && (
        <div ref={nav.listRef} className="absolute z-10 mt-1 w-full rounded-lg border border-ui-line bg-ui-card shadow-xl overflow-hidden">
          {filtered.map((p, idx) => (
            <button key={p.id} data-testid={`pick-${p.id}`} data-nav-index={idx} onMouseEnter={() => nav.setActive(idx)} onClick={() => { onPick(p); setSearch(""); }} className={`w-full text-left px-3 py-2 flex items-center justify-between ${idx === nav.active ? "bg-ui-muted-surface" : "hover:bg-ui-muted-surface"}`}>
              <span className="text-sm">{p.name}</span>
              <span className="text-xs text-ui-muted font-mono">stok {p.stock}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OpnameTab({ products, categories, activeName, onDone }) {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [step, setStep] = useState('select');
  const [confirmPost, setConfirmPost] = useState(false);
  const [zeroConfirmed, setZeroConfirmed] = useState(false);
  const posting = useRef(false);
  const [draft, setDraft] = useState(null);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [savedSignature, setSavedSignature] = useState('');
  const createRequest = useRef(null), updateRequest = useRef(null);
  const payload = { items: lines.map(l => ({ product_id:l.product_id, before:Number(l.before), counted:Number(l.counted) })), note:note || '' };
  const signature = JSON.stringify(payload);
  const dirty = (lines.length > 0 || !!note) && signature !== savedSignature;
  useEffect(() => {
    if (!dirty) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload',warn);
    return () => window.removeEventListener('beforeunload',warn);
  }, [dirty]);
  const invalidCount = lines.some(l => String(l.counted).trim() === '' || !Number.isSafeInteger(Number(l.counted)) || Number(l.counted) < 0);
  const zeroCount = lines.filter(l => Number(l.counted) === 0).length;
  const resetEditor = () => {
    setLines([]); setNote(''); setDraft(null); setSavedSignature(''); setOpen(false); setStep('select');
    createRequest.current = null; updateRequest.current = null;
  };
  const openDraft = async (row, discard = false) => {
    if (dirty && !discard) { setPendingDraft(row); return; }
    if (posting.current) return;
    posting.current = true; setSaving(true);
    try {
      const { data } = await api.get(`/opname-drafts/${row.id}`);
      if (data.state !== 'draft') { setHistoryVersion(v => v + 1); return toast.error('Opnam sudah diposting. Buka nota pada histori terbaru.'); }
      setDraft(data); setLines(data.items); setNote(data.note || ''); setStep('review'); setOpen(true);
      setSavedSignature(JSON.stringify({ items:data.items.map(l => ({ product_id:l.product_id,before:Number(l.before),counted:Number(l.counted) })),note:data.note || '' }));
      updateRequest.current = null; onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { posting.current = false; setSaving(false); }
  };
  const persistDraft = async () => {
    if (draft && signature === savedSignature) return draft;
    let response;
    if (draft) {
      const key = `${draft.id}:${draft.revision}:${signature}`;
      if (updateRequest.current?.key !== key) updateRequest.current = { key, request_id:crypto.randomUUID() };
      response = await api.put(`/opname-drafts/${draft.id}`, { ...payload, revision:draft.revision, request_id:updateRequest.current.request_id });
    } else {
      if (!createRequest.current) createRequest.current = crypto.randomUUID();
      response = await api.post('/opname-drafts', { ...payload, revision:0, request_id:createRequest.current });
    }
    if (response.data.state !== 'draft') throw new Error('Draf ini sudah diposting. Buka histori terbaru.');
    setDraft(response.data); setSavedSignature(signature); setHistoryVersion(v => v + 1);
    return response.data;
  };
  const saveDraft = async () => {
    if (posting.current || !lines.length || invalidCount) return;
    posting.current = true; setSaving(true);
    try {
      const saved = await persistDraft();
      toast.success(`Draf ${saved.opname_no} tersimpan di server. Stok belum berubah.`);
      resetEditor(); setHistoryVersion(v => v + 1);
    } catch (e) { toast.error(apiError(e)); }
    finally { posting.current = false; setSaving(false); }
  };

  const addLine = (p) => setLines((prev) => (prev.find((l) => l.product_id === p.id) ? prev : [{ product_id: p.id, name: p.name, unit: p.base_unit, before: p.stock_physical ?? p.stock, counted: 0, cost: p.cost || 0 }, ...prev]));
  const setCounted = (i, v) => setLines((prev) => prev.map((l, x) => (x === i ? { ...l, counted: v } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, x) => x !== i));

  const totals = useMemo(() => lines.reduce((a, l) => {
    const d = Number(l.counted || 0) - Number(l.before || 0);
    return { qty: a.qty + d, value: a.value + d * Number(l.cost || 0) };
  }, { qty: 0, value: 0 }), [lines]);

  const submit = async () => {
    if (posting.current) return;
    if (lines.length === 0) return toast.error("Tambahkan minimal 1 barang");
    if (invalidCount) return toast.error('Isi semua stok aktual dengan angka bulat 0 atau lebih');
    if (zeroCount && !zeroConfirmed) return;
    posting.current = true;
    setSaving(true);
    setConfirmPost(false);
    try {
      const saved = await persistDraft();
      const approval = await approveAction("stock_correction", `Opname ${lines.length} barang di ${activeName} · selisih ${totals.qty} satuan dasar`);
      if (approval === false) return;
      const { data: result } = await api.post(`/opname-drafts/${saved.id}/post`, { revision:saved.revision, confirmed:true }, approval);
      const data = result.opname;
      toast.success(`Opname ${data.opname_no} tersimpan · selisih ${data.total_diff_qty} pcs (${rupiah(data.total_diff_value)})`);
      resetEditor(); setHistoryVersion(v => v + 1); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { posting.current = false; setSaving(false); }
  };

  return (
    <>
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-semibold text-lg">Histori Stok Opnam</h2><p className="text-sm text-ui-muted">Hasil opnam tersimpan di {activeName}. Klik nomor untuk melihat rincian.</p></div>
        <WriteButton data-testid="add-opname" disabled={saving} onClick={() => setOpen(true)} className="gap-2"><PackagePlus className="h-4 w-4" />{lines.length || note ? 'Lanjutkan Stok Opnam' : 'Tambah Stok Opnam'}</WriteButton>
      </div>
      {(lines.length > 0 || note) && <p className="text-sm text-ui-muted">{dirty ? 'Ada perubahan belum disimpan ke server.' : `Draf ${draft?.opname_no || ''} sudah tersimpan di server.`} ({lines.length} barang). Buka kembali untuk melanjutkan. Stok belum berubah.</p>}
      <OpnameHistory refresh={historyVersion} onEditDraft={openDraft} disabled={saving} />
    </div>
    <Dialog open={open} onOpenChange={value => { if (!saving) setOpen(value); }}>
    <DialogContent data-testid="opname-editor" className="flex flex-col gap-0 p-0 w-full max-w-none h-[100dvh] sm:w-[calc(100%-2rem)] sm:max-w-6xl sm:h-[92dvh] overflow-hidden bg-ui-soft border-ui-line" onInteractOutside={event => event.preventDefault()}>
    <div className="shrink-0 border-b border-ui-line px-4 py-4 sm:px-6 pr-12">
      <DialogTitle>{draft ? `Draf ${draft.opname_no}` : 'Tambah Stok Opnam'}</DialogTitle>
      <DialogDescription className="mt-2">{activeName} · {step === 'select' ? '1. Pilih produk dan isi stok aktual' : '2. Periksa daftar sebelum posting'}. Stok belum berubah sampai diposting.</DialogDescription>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="opname-top-actions">
        <Button variant={step === 'select' ? 'default' : 'outline'} disabled={saving} onClick={() => setStep('select')} data-testid="opname-more-products">Pilih Produk</Button>
        <Button variant={step === 'review' ? 'default' : 'outline'} disabled={saving} onClick={() => setStep('review')} data-testid="opname-review">Draf ({lines.length})</Button>
        <WriteButton variant="outline" data-testid="opname-save-draft" onClick={saveDraft} disabled={saving || !lines.length || invalidCount}>Simpan Draf</WriteButton>
        <WriteButton data-testid="save-opname-btn" onClick={() => { setStep('review'); setZeroConfirmed(false); setConfirmPost(true); }} disabled={saving || !lines.length || invalidCount} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">{saving ? 'Memposting…' : 'Posting Stok Opnam'}</WriteButton>
        <Button variant="outline" disabled={saving} onClick={() => setOpen(false)} data-testid="close-opname-editor">Tutup</Button>
      </div>
    </div>
    {approvalDialog}
    <div className="flex-1 min-h-0 min-w-0 overflow-y-auto p-3 sm:p-5 space-y-4">
      <div hidden={step !== 'select'}>
      <OpnameSelection products={products} categories={categories} activeName={activeName} disabled={saving} draftLines={lines} onDraftChange={setLines} />
      </div>
      <div hidden={step !== 'review'} className="space-y-4">
      <div><h2 className="font-semibold">Draf Produk Opnam ({lines.length})</h2><p className="text-sm text-ui-muted">Produk terbaru di atas. Stok aktual adalah jumlah akhir, bukan penambahan stok. Periksa juga produk bernilai 0.</p></div>
      <fieldset disabled={saving} className="space-y-4 min-w-0">
      <ProductPicker products={products} exclude={lines.map((l) => l.product_id)} onPick={addLine} testid="opname-search" />
      <DraftExcelTools kind="opname" existingIds={lines.map(l => l.product_id)} disabled={saving} onAppend={rows => {
        const imported = rows.map(row => {
          const p = products.find(product => product.id === row.product_id);
          if (!p || row.pieces !== 1 || row.unit.toLowerCase() !== (p.base_unit || 'Pcs').toLowerCase()) throw new Error(`Opname ${row.name}: gunakan satuan dasar produk, bukan kemasan.`);
          return { product_id:p.id, name:p.name, unit:p.base_unit, before:p.stock_physical ?? p.stock, counted:row.qty, cost:p.cost || 0 };
        });
        setLines(previous => [...imported, ...previous]);
      }} />
      <div className="rounded-xl border border-ui-line overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-ui-card text-ui-muted text-xs uppercase">
            <tr>
              <th className="text-left p-2 pl-3">Barang</th>
              <th className="text-center p-2 w-24">Stok Sistem</th>
              <th className="text-center p-2 w-28">Stok Aktual</th>
              <th className="text-right p-2 w-28">Selisih</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody data-testid="opname-lines">
            {lines.map((l, i) => {
              const diff = Number(l.counted || 0) - Number(l.before || 0);
              return (
                <tr key={l.product_id} data-product-id={l.product_id} className="border-t border-ui-line">
                  <td className="p-2 pl-3">{l.name}<small className="block text-ui-muted">Satuan dasar: {l.unit || 'Pcs'}</small></td>
                  <td className="p-2 text-center font-mono text-ui-muted">{l.before}</td>
                  <td className="p-2"><Input data-testid={`opname-counted-${i}`} aria-label={`Stok aktual ${l.name}`} type="number" min="0" step="1" inputMode="numeric" value={l.counted} onFocus={e => e.target.select()} onChange={(e) => setCounted(i, e.target.value)} className="h-9 text-center font-mono bg-ui-soft border-ui-line" /></td>
                  <td className={`p-2 text-right font-mono font-bold ${diff > 0 ? "text-brand-readable" : diff < 0 ? "text-danger-readable" : "text-ui-muted"}`}>{diff > 0 ? `+${diff}` : diff}</td>
                  <td className="p-2 text-center"><button data-testid={`opname-remove-${i}`} onClick={() => removeLine(i)} className="text-danger-readable hover:text-danger-readable"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
            {lines.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ui-muted"><PackagePlus className="h-6 w-6 mx-auto mb-1" /> Belum ada barang</td></tr>}
          </tbody>
        </table>
      </div>
      <Textarea data-testid="opname-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan opname (opsional)" className="bg-ui-card border-ui-line min-h-[38px]" rows={1} />
      </fieldset>
      {invalidCount && <p role="alert" className="text-sm text-danger-readable">Isi semua stok aktual dengan angka bulat 0 atau lebih. Kolom kosong belum dianggap nol.</p>}
      <p className="text-xs text-ui-muted" data-testid="opname-save-status">{draft && !dirty ? `Tersimpan di server · ${draft.opname_no} · revisi ${draft.revision}. Stok belum berubah.` : 'Perubahan belum disimpan. Tekan Simpan Draf agar dapat dilanjutkan dari histori setelah muat ulang.'}</p>
      </div>
    </div>
      <div className="shrink-0 flex flex-wrap gap-3 items-center justify-between bg-ui-card border-t border-ui-line px-4 py-3">
        <span className="text-ui-muted text-sm">{lines.length} produk · Selisih: <b data-testid="opname-total-qty" className="text-ui-text">{totals.qty}</b> · <span data-testid="opname-total-value" className={totals.value < 0 ? "text-danger-readable" : "text-brand-readable"}>{rupiah(totals.value)}</span></span>
      </div>
    <AlertDialog open={confirmPost} onOpenChange={setConfirmPost}><AlertDialogContent className="w-[calc(100%-2rem)] max-h-[90dvh] overflow-y-auto bg-ui-card border-ui-line" data-testid="opname-confirm-post">
      <AlertDialogTitle>Posting Stok Opnam?</AlertDialogTitle>
      <AlertDialogDescription>{lines.length} produk di {activeName} akan diganti stoknya sesuai stok aktual. Setelah posting, nota ini tidak dapat diedit. Produk yang tidak ada dalam daftar tidak diubah.</AlertDialogDescription>
      {zeroCount > 0 && <><p className="text-sm text-danger-readable">{zeroCount} produk memiliki QTY 0. Stok produk tersebut akan menjadi nol.</p><label className="flex items-start gap-2 text-sm"><Checkbox checked={zeroConfirmed} onCheckedChange={value => setZeroConfirmed(value === true)} data-testid="opname-confirm-zero" />Saya sudah memeriksa produk dengan stok aktual 0.</label></>}
      <AlertDialogFooter><AlertDialogCancel data-testid="opname-cancel-post">Periksa Lagi</AlertDialogCancel><AlertDialogAction data-testid="opname-post-now" disabled={saving || (zeroCount > 0 && !zeroConfirmed)} onClick={event => { event.preventDefault(); submit(); }}>Ya, Posting Sekarang</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent></AlertDialog>
    </DialogContent>
    </Dialog>
    <AlertDialog open={!!pendingDraft} onOpenChange={value => { if (!value) setPendingDraft(null); }}><AlertDialogContent className="w-[calc(100%-2rem)] bg-ui-card border-ui-line" data-testid="opname-open-server-confirm">
      <AlertDialogTitle>Buka draf dari server?</AlertDialogTitle>
      <AlertDialogDescription>Ada perubahan di halaman yang belum disimpan. Membuka versi server akan mengganti isian tersebut. Pilih Kembali jika ingin menyimpan perubahan terlebih dahulu.</AlertDialogDescription>
      <AlertDialogFooter><AlertDialogCancel>Kembali</AlertDialogCancel><AlertDialogAction data-testid="opname-open-server" onClick={() => { const row = pendingDraft; setPendingDraft(null); openDraft(row, true); }}>Buka Versi Server</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent></AlertDialog>
    </>
  );
}
